import { forwardRef, useState } from "react";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import PropTypes from "prop-types";

const isPromiseLike = (value) => {
  return value != null && typeof value.then === "function";
};

const BusyButton = forwardRef(function BusyButton(
  {
    busy = false,
    busyIndicatorSize = 18,
    children,
    disabled = false,
    disableWhileBusy = true,
    onClick,
    showBusyWhenDisabled = true,
    ...buttonProps
  },
  ref,
) {
  const [internalBusy, setInternalBusy] = useState(false);

  const showBusy = busy || internalBusy || (showBusyWhenDisabled && disabled);
  const isDisabled = disabled || (disableWhileBusy && (busy || internalBusy));

  const handleClick = async (event) => {
    if (!onClick || isDisabled) {
      return;
    }

    setInternalBusy(true);

    try {
      const result = onClick(event);

      if (isPromiseLike(result)) {
        await result;
      }
    } finally {
      setInternalBusy(false);
    }
  };

  return (
    <Button
      {...buttonProps}
      ref={ref}
      disabled={isDisabled}
      onClick={handleClick}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        {showBusy && (
          <CircularProgress
            color="inherit"
            size={busyIndicatorSize}
            thickness={5}
          />
        )}
        <span>{children}</span>
      </Box>
    </Button>
  );
});

BusyButton.propTypes = {
  busy: PropTypes.bool,
  busyIndicatorSize: PropTypes.number,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  disableWhileBusy: PropTypes.bool,
  onClick: PropTypes.func,
  showBusyWhenDisabled: PropTypes.bool,
};

export default BusyButton;
